import { useEffect, useMemo, useState } from 'react';
import type { MediaItem, Playlist } from '@cornerstone/shared';
import { api } from '../client/api';
import { cn } from '../utils/cn';
import { Button } from './ui';
import { backdropCloseProps, useDialogPanel } from './menu';

// =============================================================================
// TargetPickerModal — thumbnail-grid picker for Event target selection.
// Replaces the plain <select> dropdown with a visual grid that shows
// thumbnails + type badges. Two modes based on `kind`:
//   'media'    — renders MediaItem tiles
//   'playlist' — renders Playlist cards with first-item thumbnail preview
// =============================================================================

type MediaPicker = {
  kind: 'media';
  items: MediaItem[];
  selectedId: string;
  onSelect: (id: string) => void;
};

type PlaylistPicker = {
  kind: 'playlist';
  playlists: Playlist[];
  mediaById: Map<string, MediaItem>;
  selectedId: string;
  onSelect: (id: string) => void;
};

type Props =
  | (MediaPicker & { onClose: () => void })
  | (PlaylistPicker & { onClose: () => void });

export function TargetPickerModal(props: Props) {
  const [query, setQuery] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') props.onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [props]);

  const filteredMedia = useMemo(() => {
    if (props.kind !== 'media') return [];
    const q = query.trim().toLowerCase();
    if (!q) return props.items;
    return props.items.filter((m) => m.originalName.toLowerCase().includes(q));
  }, [props, query]);

  const filteredPlaylists = useMemo(() => {
    if (props.kind !== 'playlist') return [];
    const q = query.trim().toLowerCase();
    if (!q) return props.playlists;
    return props.playlists.filter((p) => p.name.toLowerCase().includes(q));
  }, [props, query]);

  const title = props.kind === 'media' ? '미디어 선택' : '플레이리스트 선택';
  const panel = useDialogPanel<HTMLDivElement>();

  return (
    <div
      data-modal-backdrop="full"
      className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-8"
      {...backdropCloseProps(props.onClose)}
    >
      <div
        {...panel}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'bg-panel border border-line-strong rounded-md shadow-[0_20px_60px_rgba(0,0,0,0.6)] outline-none',
          'w-[1056px] max-w-full max-h-[min(720px,100vh-80px)] flex flex-col overflow-hidden',
        )}
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-line bg-panel-2">
          <div className="flex items-baseline gap-3">
            <span className="font-display text-[19px] font-semibold">{title}</span>
            <span className="font-mono text-[12px] uppercase tracking-monoWide text-text-dim">
              {props.kind === 'media'
                ? `${props.items.length} items`
                : `${props.playlists.length} playlists`}
            </span>
          </div>
          <button
            type="button"
            onClick={props.onClose}
            aria-label="닫기" data-modal-close
            className="w-8 h-8 flex items-center justify-center text-text-dim hover:text-text rounded-sm"
          >
            ✕
          </button>
        </header>

        <div className="px-5 py-3 border-b border-line bg-panel-2">
          <input
            type="text"
            placeholder="Filter…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full px-3 py-2 bg-bg border border-line rounded text-[16px] text-text"
            autoFocus
          />
        </div>

        <div className="flex-1 overflow-auto p-4">
          {props.kind === 'media' ? (
            filteredMedia.length === 0 ? (
              <EmptyNote>No media. Upload from the Media tab first.</EmptyNote>
            ) : (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(170px,1fr))] gap-2">
                {filteredMedia.map((m) => (
                  <MediaTile
                    key={m.id}
                    item={m}
                    selected={m.id === props.selectedId}
                    onClick={() => {
                      props.onSelect(m.id);
                      props.onClose();
                    }}
                  />
                ))}
              </div>
            )
          ) : filteredPlaylists.length === 0 ? (
            <EmptyNote>No playlists. Create one in the Media tab.</EmptyNote>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-2">
              {filteredPlaylists.map((p) => (
                <PlaylistCard
                  key={p.id}
                  playlist={p}
                  mediaById={props.mediaById}
                  selected={p.id === props.selectedId}
                  onClick={() => {
                    props.onSelect(p.id);
                    props.onClose();
                  }}
                />
              ))}
            </div>
          )}
        </div>

        <footer className="flex justify-end px-5 py-3 border-t border-line bg-panel-2">
          <Button onClick={props.onClose}>닫기</Button>
        </footer>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Tiles
// -----------------------------------------------------------------------------

function MediaTile({
  item,
  selected,
  onClick,
}: {
  item: MediaItem;
  selected: boolean;
  onClick: () => void;
}) {
  const hasThumb = item.thumbnailRelPath !== null;
  const isImage = item.mediaType === 'image';
  const thumbUrl = hasThumb
    ? api.mediaThumbnailUrl(item.id)
    : isImage
      ? api.mediaFileUrl(item.id)
      : null;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group text-left bg-panel-2 border rounded overflow-hidden transition-colors duration-fast',
        selected ? 'border-accent' : 'border-line hover:border-accent-ring',
      )}
    >
      <div className="aspect-video bg-black relative">
        {thumbUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={thumbUrl}
            alt=""
            className="w-full h-full object-cover"
            draggable={false}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center font-mono text-[12px] text-text-dim">
            {item.mediaType.toUpperCase()}
          </div>
        )}
        <span className="absolute top-1.5 left-1.5 font-mono text-[11px] uppercase tracking-monoWide bg-black/70 text-text px-1.5 py-[1px] rounded-sm">
          {item.mediaType}
        </span>
        {selected && (
          <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-accent text-bg text-[14px] font-bold flex items-center justify-center">
            ✓
          </span>
        )}
      </div>
      <div className="px-2.5 py-1.5 text-[13px] text-text truncate">{item.originalName}</div>
    </button>
  );
}

function PlaylistCard({
  playlist,
  mediaById,
  selected,
  onClick,
}: {
  playlist: Playlist;
  mediaById: Map<string, MediaItem>;
  selected: boolean;
  onClick: () => void;
}) {
  // Up to 4 item thumbnails for a visual summary. Skip notice items (no
  // media thumbnail available here).
  const thumbs = playlist.items
    .slice(0, 4)
    .map((it) => (it.mediaId ? mediaById.get(it.mediaId) : undefined))
    .filter((m): m is MediaItem => m !== undefined);

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'text-left bg-panel-2 border rounded overflow-hidden transition-colors duration-fast',
        selected ? 'border-accent' : 'border-line hover:border-accent-ring',
      )}
    >
      <div className="grid grid-cols-2 grid-rows-2 aspect-video bg-black relative">
        {thumbs.length === 0 ? (
          <div className="col-span-2 row-span-2 flex items-center justify-center font-mono text-[12px] text-text-dim">
            EMPTY
          </div>
        ) : (
          thumbs.map((m, i) => (
            <div key={`${m.id}-${i}`} className="bg-black relative overflow-hidden">
              {m.thumbnailRelPath ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={api.mediaThumbnailUrl(m.id)}
                  alt=""
                  className="w-full h-full object-cover"
                  draggable={false}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center font-mono text-[10px] text-text-dim">
                  {m.mediaType}
                </div>
              )}
            </div>
          ))
        )}
        {selected && (
          <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-accent text-bg text-[14px] font-bold flex items-center justify-center">
            ✓
          </span>
        )}
      </div>
      <div className="px-2.5 py-1.5">
        <div className="text-[14px] text-text font-medium truncate">{playlist.name}</div>
        <div className="font-mono text-[12px] text-text-dim">
          {playlist.items.length} items
        </div>
      </div>
    </button>
  );
}

function EmptyNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="h-full flex items-center justify-center text-center font-mono text-[13px] text-text-dimmer py-12">
      {children}
    </div>
  );
}
